import clickSoundFile from '../assets/sound/click-sound.mp3';

const STORAGE_KEYS = {
  typing: 'typingMatrixSoundEnabled',
  click: 'typingMatrixClickSoundEnabled'
};

// Sound effects using Web Audio API
class SoundEffects {
  constructor() {
    this.audioContext = null;
    this.enabled = this.getStored('typing', true);
    this.clickEnabled = this.getStored('click', true);
    this.clickAudio = null;
  }

  getStored(key, fallback) {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS[key]);
      return stored === null ? fallback : stored === 'true';
    } catch (error) {
      return fallback;
    }
  }

  setStored(key, value) {
    try {
      localStorage.setItem(STORAGE_KEYS[key], String(value));
    } catch (error) {
      // localStorage unavailable, ignore
    }
  }

  initAudioContext() {
    if (this.audioContext) return;
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    } catch (error) {
      this.enabled = false;
    }
  }

  initClickAudio() {
    if (this.clickAudio) return;
    try {
      this.clickAudio = new Audio(clickSoundFile);
      this.clickAudio.volume = 0.5;
      this.clickAudio.preload = 'none';
    } catch (error) {
      this.clickAudio = null;
    }
  }

  // Play the mp3 click sound (fires on every UI click)
  playClickSound() {
    if (!this.clickEnabled) return;
    if (!this.clickAudio) this.initClickAudio();
    if (!this.clickAudio) return;
    try {
      this.clickAudio.currentTime = 0;
      const playPromise = this.clickAudio.play();
      if (playPromise) {
        playPromise.catch(() => {});
      }
    } catch (error) {
      // ignore playback errors
    }
  }

  // Create a beep sound
  createBeep(frequency = 800, duration = 100, volume = 0.1) {
    if (!this.enabled) return;
    if (!this.audioContext) this.initAudioContext();
    if (!this.audioContext) return;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
    oscillator.type = 'square';

    gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(volume, this.audioContext.currentTime + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + duration / 1000);

    oscillator.start(this.audioContext.currentTime);
    oscillator.stop(this.audioContext.currentTime + duration / 1000);
  }

  // Typing sound
  playTypingSound() {
    this.createBeep(1200, 50, 0.05);
  }

  // Correct character sound
  playCorrectSound() {
    this.createBeep(1500, 80, 0.08);
  }

  // Error sound
  playErrorSound() {
    this.createBeep(300, 200, 0.1);
  }

  // Success sound (completion)
  playSuccessSound() {
    // Play a sequence of ascending notes
    const notes = [523, 659, 784, 1047]; // C, E, G, C
    notes.forEach((freq, index) => {
      setTimeout(() => {
        this.createBeep(freq, 300, 0.15);
      }, index * 150);
    });
  }

  // Achievement sound
  playAchievementSound() {
    // Play a triumphant chord
    const frequencies = [523, 659, 784]; // C major chord
    frequencies.forEach(freq => {
      this.createBeep(freq, 500, 0.1);
    });
  }

  // Toggle typing/game sounds
  toggle() {
    this.enabled = !this.enabled;
    this.setStored('typing', this.enabled);
    return this.enabled;
  }

  // Toggle click sound
  toggleClick() {
    this.clickEnabled = !this.clickEnabled;
    this.setStored('click', this.clickEnabled);
    return this.clickEnabled;
  }

  // Resume audio context (needed for some browsers)
  resume() {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }
  }
}

export default new SoundEffects();
